import React, { useState, useEffect, useRef } from "react";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import LiveFeedUtils from "Pages/Dashboard/dashboard/LiveFeed/utils";
import { months } from "Pages/Dashboard/dashboard/LiveFeed/constants";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";

class WaReplyPopup {
  @observable showPopup = false;
  @observable messages = [];
  @observable editorLoading = true;
  @observable ssId = "";
  @observable chatId = "";

  @action setShowPopup = (value = false) => { this.showPopup = value; };
  @action setMessages = (value = []) => { this.messages = value; };
  @action setEditorLoading = (value = false) => { this.editorLoading = value; };
  @action setSsId = (value = "") => { this.ssId = value; };
  @action setChatId = (value = "") => { this.chatId = value; };
}

const waReplyPopupState = new WaReplyPopup();

const formatTime = value => {
  const date = new Date(value);
  if (!value || isNaN(date.getTime())) return "";
  const hours = ((date.getHours() + 11) % 12) + 1;
  const mins = date.getMinutes();
  const meridiem = date.getHours() >= 12 ? "PM" : "AM";
  const h = hours === 12 && meridiem === "AM" ? "00" : hours;
  if (LiveFeedUtils.isYesterday(date)) {
    return `Yesterday ${h}:${mins > 9 ? mins : "0" + mins} ${meridiem}`;
  } else if (LiveFeedUtils.isToday(date)) {
    return `${h}:${mins > 9 ? mins : "0" + mins} ${meridiem}`;
  }
  const mon = months[date.getMonth()];
  return `${mon} ${date.getDate()}, ${h}:${mins > 9 ? mins : "0" + mins} ${meridiem}`;
};

const sortByTimestamp = msgs =>
  [...msgs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

const WaReplyContentPopup = observer(() => {
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (!waReplyPopupState.editorLoading) {
      scrollToBottom();
    }
  }, [waReplyPopupState.editorLoading, waReplyPopupState.messages.length]);

  const close = () => {
    waReplyPopupState.setShowPopup(false);
    waReplyPopupState.setMessages([]);
    waReplyPopupState.setSsId("");
    waReplyPopupState.setChatId("");
    setReplyText("");
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  const handleTextareaChange = e => {
    setReplyText(e.target.value);
    // Auto-resize
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 100) + "px";
  };

  const sendReply = async () => {
    if (isSending || !replyText.trim()) return;
    const chatId = waReplyPopupState.chatId;
    if (!chatId) {
      toasterState.setToastMsg("No chat ID found for this contact", "failure");
      return;
    }
    setIsSending(true);
    try {
      const config = {
        url: URLS.sendWhatsAppReplyMessage,
        method: "POST",
        data: { chatId, text: replyText.trim() }
      };
      const res = await makeApi(config);
      const responseCode = +res?.data?.responseCode;
      if (responseCode === 200) {
        waReplyPopupState.setMessages([
          ...waReplyPopupState.messages,
          {
            message: replyText.trim(),
            isSender: true,
            timestamp: new Date().toISOString(),
            senderName: "You"
          }
        ]);
        setReplyText("");
        setTimeout(scrollToBottom, 50);
      } else {
        toasterState.setToastMsg("Failed to send message", "failure");
      }
    } catch {
      toasterState.setToastMsg("Error sending message", "failure");
    } finally {
      setIsSending(false);
    }
  };

  const sorted = sortByTimestamp(waReplyPopupState.messages);

  return (
    waReplyPopupState.showPopup && (
      <>
        <div className="waPopupWrapper">
          <div className="waPopupCard">
            {/* Header */}
            <div className="waPopupHeader">
              <div className="waPopupHeaderLeft">
                <div className="waAvatarCircle">
                  <i className="material-icons">chat</i>
                </div>
                <div className="waPopupHeaderInfo">
                  <span className="waPopupTitle">WhatsApp</span>
                  <span className="waPopupSubtitle">Conversation</span>
                </div>
              </div>
              <div className="waPopupClose" onClick={close}>
                <i className="material-icons">close</i>
              </div>
            </div>

            {/* Chat area */}
            <div className="waPopupBody">
              {waReplyPopupState.editorLoading ? (
                <div className="waLoadingWrapper">
                  <div className="waSpinner" />
                  <span className="waLoadingText">Loading conversation…</span>
                </div>
              ) : sorted.length === 0 ? (
                <div className="waEmptyState">
                  <i className="material-icons">chat_bubble_outline</i>
                  <p>No messages found</p>
                </div>
              ) : (
                <>
                  {sorted.map((msg, idx) => {
                    const {
                      message = "",
                      isSender = false,
                      timestamp = ""
                    } = msg || {};

                    // Group date separator
                    const prevMsg = sorted[idx - 1];
                    const showDate =
                      idx === 0 ||
                      new Date(timestamp).toDateString() !==
                        new Date(prevMsg?.timestamp).toDateString();

                    return (
                      <React.Fragment key={idx}>
                        {showDate && timestamp && (
                          <div className="waDateSeparator">
                            <span>{new Date(timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                          </div>
                        )}
                        <div className={`waBubbleRow ${isSender ? "waBubbleRowSent" : "waBubbleRowReceived"}`}>
                          <div className={`waBubble ${isSender ? "waBubbleSent" : "waBubbleReceived"}`}>
                            <p className="waBubbleText">{message}</p>
                            <span className="waBubbleTime">{formatTime(timestamp)}</span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Reply input */}
            <div className="waPopupInputBar">
              <textarea
                ref={textareaRef}
                className="waInputTextarea"
                placeholder="Type a message"
                value={replyText}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={isSending}
              />
              <button
                className={`waSendBtn ${!replyText.trim() || isSending ? "waSendBtnDisabled" : ""}`}
                onClick={sendReply}
                disabled={!replyText.trim() || isSending}
              >
                {isSending ? (
                  <div className="waSendSpinner" />
                ) : (
                  <i className="material-icons">send</i>
                )}
              </button>
            </div>
          </div>
          <div className="waPopupOverlay" onClick={close} />
        </div>
      </>
    )
  );
});

export { WaReplyContentPopup, waReplyPopupState };
export default WaReplyContentPopup;
